import { useMutation } from '@apollo/client';
import type {
  InsertEnterpriseRiskMutation,
  InsertEnterpriseRiskMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertEnterpriseRiskTRPC } from './useInsertEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (RSP-6498). Delegates to the tRPC twin when the `trpc`
 * flag is on, else the legacy Apollo Hasura-action path. Consumers import THIS,
 * never the *TRPC twin (see packages/web/CLAUDE.md → "Mutation Hook Wrapper
 * Pattern").
 */
export const useInsertEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcMutation = useInsertEnterpriseRiskTRPC();

  const [insertGraphQL, graphqlState] = useMutation(
    InsertEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'enterprise_risk');
      },
      refetchQueries: [
        namedOperations.Query.getEnterpriseRiskById,
        namedOperations.Query.getEnterpriseRisksFlat,
        namedOperations.Query.getEnterpriseRisksByTier,
        namedOperations.Query.getEnterpriseRisks,
      ],
    }
  );

  const insertEnterpriseRisk = async (
    variables: InsertEnterpriseRiskMutationVariables
  ): Promise<InsertEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.insertEnterpriseRisk(variables);
    }

    const result = await insertGraphQL({ variables });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      insertEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertEnterpriseRisk,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
